import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"

import { connect } from "react-redux"

import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"
import type { PagingOptions } from "../../../../../models"
import { changeLicencePage, fetchAllLicences, removeLicence } from "../../../../../redux"
import type { AppState, AppThunkDispatch, Licence, LicenceAction } from "../../../../../redux"
import { CreateButton } from "../../../../atoms"
import { CoMaPagingBar, CrudDeleteDialog, CrudTableHeader, PageContainer } from "../../../../molecules"

export interface ILicenceManagerOverviewPageProps {
  licences: Licence[]
  pagingOptions: PagingOptions
  fetchLicences: () => void
  deleteLicence: (licenceId: string) => void
  changePage: (page: number) => void
}

function LicenceManagerOverviewPageView(props: ILicenceManagerOverviewPageProps): ReactElement {
  const { licences, pagingOptions, fetchLicences, deleteLicence, changePage } = props

  const { t } = useTranslation("contentManager")

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Licence)

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined)
  const history = useHistory()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchLicences()
  }, [])

  function navigateToEditForm(routeParam: string): void {
    history.push(`licences/${routeParam}`)
  }

  function onPageChange(page: number): void {
    if (page < 0 || page >= pagingOptions.totalPages || page === pagingOptions.currentPage) {
      return
    }
    changePage(page)
  }

  function onDeleteButtonClicked(id: string) {
    setDialogOpen(true)
    setIdToRemove(id)
  }

  function onDialogClose(): void {
    setDialogOpen(false)
    setIdToRemove(undefined)
  }

  function onDeleteConfirm(): void {
    if (idToRemove) deleteLicence(idToRemove)
    onDialogClose()
  }

  return (
    <PageContainer id="LicenceManagerOverview" noPadding>
      <CreateButton onClick={() => navigateToEditForm("create")} />
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <TableContainer>
        <Table>
          <CrudTableHeader
            headers={[
              { key: "name", label: t("licences.table.name") },
              {
                key: "ean",
                label: t("licences.table.licenceEAN"),
                disableSort: true
              }
            ]}
            onChange={fetchLicences}
          />
          <TableBody>
            {licences?.map((licence: Licence, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <TableRow key={index}>
                <TableCell>{licence.name}</TableCell>
                <TableCell>{licence.ean}</TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => navigateToEditForm(licence.id as string)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onDeleteButtonClicked(licence.id as string)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <CrudDeleteDialog open={dialogOpen} onClose={onDialogClose} onConfirm={onDeleteConfirm} />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    licences: state.licences.licences,
    pagingOptions: state.licences.pagingOptions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<LicenceAction>) {
  return {
    fetchLicences: () => dispatch(fetchAllLicences()),
    deleteLicence: (licenceId: string) => dispatch(removeLicence(licenceId)),
    changePage: (page: number) => dispatch(changeLicencePage(page))
  }
}

export const LicenceManagerOverviewPage = connect(mapStateToProps, mapDispatchToProps)(LicenceManagerOverviewPageView)
